import { AntInput } from "@/CommonElements/AntInput";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { Button, Stack } from "react-bootstrap";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { Badge, Container, Spinner } from "reactstrap";
import { validateArabicAndNumbersOnly, validateEnglishAndNumbersOnly } from "../ProfileForm";
import { cn, PickKeyPairs } from "@/utils/helpers";
import { Switch } from "antd";
import {
  useCreateExaminationPeriodMutation,
  useGetOneExaminationPeriodQuery,
  useUpdateExaminationPeriodMutation,
} from "@/services/basicData";
import { useEffect } from "react";

export const ExaminationPeriodPage = () => {
  // ==============
  // hooks
  // ==============
  const { t } = useTranslation();
  const { mode } = useParams();
  const isViewMode = mode === "view";

  const form = useForm({
    defaultValues: {
      is_active: true,
    },
  });
  const {
    control,
    formState: { errors },
  } = form;

  const { handleSubmit, setError } = form;

  // ==============
  // Side Effects
  // =============

  const {
    mutate: createExaminationPeriod,
    isPending: isCreateExaminationPeriodPending,
  } = useCreateExaminationPeriodMutation(setError);
  const {
    mutate: updateExaminationPeriod,
    isPending: isUpdateExaminationPeriodPending,
  } = useUpdateExaminationPeriodMutation(setError);

  const onSubmit = (data) => {
    if (mode === "create") {
      createExaminationPeriod(data, {
        onSuccess: () => {
          form.reset(form.formState.defaultValues);
        },
      });
    } else {
      updateExaminationPeriod(data, {
        onSuccess: () => {
          form.reset(form.formState.defaultValues);
        },
      });
    }
  };

  const isPending =
    isCreateExaminationPeriodPending || isUpdateExaminationPeriodPending;

  const backLink =
    window.history.length > 1
      ? -1
      : `/app/makkah/basic-data/examination_periods`;

  const title = {
    view: t("VIEW_EXAMINATION_PERIOD"),
    create: t("CREATE_EXAMINATION_PERIOD"),
    edit: t("UPDATE_EXAMINATION_PERIOD"),
  };
  return (
    <Stack gap={2}>
      <PageHeader title={title[mode]}></PageHeader>
      <Container fluid>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Form */}
            <ExaminationPeriodForm control={control} errors={errors} />

            {/* Form Actions */}
            {isViewMode ? null : (
              <ActionsFooter>
                <Button
                  className="btn btn-primary"
                  type="submit"
                  disabled={isPending}
                >
                  {isPending ? <Spinner size="sm" /> : null}
                  {mode === "create" ? t("CREATE") : t("UPDATE")}
                </Button>
                <Link
                  disabled={isPending}
                  style={{ pointerEvents: isPending ? "none" : "auto" }}
                  to={backLink}
                  className="btn btn-danger"
                  type="submit"
                >
                  {t("CANCEL")}
                </Link>
              </ActionsFooter>
            )}
          </form>
        </FormProvider>
      </Container>
    </Stack>
  );
};

const ExaminationPeriodForm = () => {
  // ==============
  // hooks
  // ==============
  const form = useFormContext();
  const { id, mode } = useParams();
  const isViewMode = mode === "view";

  // ==============
  // Side Effects
  // ==============
  const { data: examinationPeriodResponse, isLoading } =
    useGetOneExaminationPeriodQuery({ id });
  const examinationPeriod = examinationPeriodResponse?.data?.data;

  useEffect(() => {
    if (examinationPeriod) {
      form.reset(
        PickKeyPairs(examinationPeriod, [
          "name_ar",
          "name_en",
          "time_from",
          "time_to",
          "is_active",
        ])
      );
    }
  }, [examinationPeriod]);

  const {
    control,
    formState: { errors },
  } = form;
  const { t } = useTranslation();

  const controllers = [
    {
      labelProps: {
        children: t("NAME_AR"),
      },
      controllerProps: {
        name: "name_ar",
        rules: {
          required: t("FIELD_REQUIRED"),
          validate: (value) => validateArabicAndNumbersOnly(value, t),
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            placeholder={t("NAME_AR")}
            status={errors.name_ar ? "error" : undefined}
          />
        ),
      },
      renderValue: (value) => value || "-",
    },
    {
      labelProps: {
        children: t("NAME_EN"),
      },
      controllerProps: {
        name: "name_en",
        rules: {
          required: t("FIELD_REQUIRED"),
          validate: (value) => validateEnglishAndNumbersOnly(value, t),
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            placeholder={t("NAME_EN")}
            status={errors.name_en ? "error" : undefined}
          />
        ),
      },
      renderValue: (value) => value || "-",
    },
    {
      labelProps: {
        children: t("TIME_FROM"),
      },
      controllerProps: {
        name: "time_from",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            type="time"
            placeholder={t("TIME_FROM")}
            status={errors.time_from ? "error" : undefined}
          />
        ),
      },
      renderValue: (value) => value || "-",
    },
    {
      labelProps: {
        children: t("TIME_TO"),
      },
      controllerProps: {
        name: "time_to",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            type="time"
            placeholder={t("TIME_TO")}
            status={errors.time_to ? "error" : undefined}
          />
        ),
      },
      renderValue: (value) => value || "-",
    },
    {
      labelProps: {
        children: t("STATUS"),
        rules: {
          required: undefined,
        },
      },
      controllerProps: {
        name: "is_active",
        render: ({ field }) => (
          <div>
            <Switch
              {...field}
              checked={field.value}
              onChange={(checked) => field.onChange(checked)}
              status={errors.is_active ? "error" : undefined}
              checkedChildren={t("ACTIVE")}
              unCheckedChildren={t("INACTIVE")}
            />
          </div>
        ),
      },
      renderValue: (value) => (
        <div
          className={cn(
            "bg-transparent px-3 py-1 rounded-pill",
            value ? "badge badge-light-success border border-light-success" : "badge badge-light-danger border border-light-danger"
          )}
          style={{ fontSize: "12px" }}
        >
          {value ? t("ACTIVE") : t("INACTIVE")}
        </div>
      ),
    },
  ];

  return (
    <Controllers
      isLoading={isLoading}
      isViewMode={isViewMode}
      watch={form.watch}
      {...{
        controllers: controllers.map((config) => ({
          ...config,
          labelProps: {
            ...config.labelProps,
            className: cn("fw-bold required", config.labelProps.className),
          },
          controllerProps: {
            ...config.controllerProps,
            rules: {
              ...(config.controllerProps.rules || {}),
            },
            control,
            errors,
          },
          layout: {
            colProps: { xs: 6, md: 6 },
          },
        })),
      }}
    />
  );
};
